import React, { useState, useEffect } from 'react';
import {
  Table, Tag, Button, Modal, Select, Input,
  Card, Row, Col, Statistic, message, Tooltip,
  Space, Badge, Descriptions, Radio, Spin
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined,
  EyeOutlined, EditOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  collection, getDocs, doc, updateDoc,
  query, orderBy, serverTimestamp, addDoc
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CARD_STYLE } from '../../constants/theme';

const { Option } = Select;
const { TextArea } = Input;

const TIER_CONFIG = {
  economy: { label: 'Economy', color: '#52C41A', bg: 'rgba(82,196,26,0.12)' },
  premium: { label: 'Premium', color: '#1890FF', bg: 'rgba(24,144,255,0.12)' },
  luxury: { label: 'Luxury', color: '#C9A84C', bg: 'rgba(201,168,76,0.12)' },
  super_luxury: { label: 'Super Luxury', color: '#722ED1', bg: 'rgba(114,46,209,0.12)' },
};

const STATUS_CONFIG = {
  pending: { color: '#FAAD14', label: 'Pending', badge: 'warning' },
  approved: { color: '#52C41A', label: 'Approved', badge: 'success' },
  rejected: { color: '#FF4D4F', label: 'Rejected', badge: 'error' },
  inactive: { color: '#7A8A96', label: 'Inactive', badge: 'default' },
};

export default function ProductApproval() {
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [tierModal, setTierModal] = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [newTier, setNewTier] = useState('');
  const [reason, setReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadProducts(); }, []);

  useEffect(() => {
    let result = products;
    if (filterStatus !== 'all') result = result.filter(p => p.status === filterStatus);
    if (filterCategory !== 'all') result = result.filter(p => p.category === filterCategory);
    setFiltered(result);
  }, [filterStatus, filterCategory, products]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'products'), orderBy('submittedAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        submittedAt: d.data().submittedAt?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
      }));
      setProducts(data);
      setFiltered(data);
    } catch (error) {
      message.error('Failed to load products');
    }
    setLoading(false);
  };

  const stats = {
    total: products.length,
    pending: products.filter(p => p.status === 'pending').length,
    approved: products.filter(p => p.status === 'approved').length,
    rejected: products.filter(p => p.status === 'rejected').length,
  };

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  const handleTierChange = async () => {
    if (!newTier) { message.warning('Please select a tier'); return; }
    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'products', selected.id), {
        assignedTier: newTier,
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'auditLogs'), {
        action: 'PRODUCT TIER CHANGED',
        details: 'Product: ' + selected.name + ' | From: ' + selected.assignedTier + ' | To: ' + newTier,
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: selected.id, targetType: 'product',
        timestamp: serverTimestamp(),
      });
      setProducts(prev => prev.map(p =>
        p.id === selected.id ? { ...p, assignedTier: newTier } : p
      ));
      message.success('Tier updated to ' + TIER_CONFIG[newTier].label + ' — saved to Firebase');
      setTierModal(false); setNewTier('');
    } catch (error) {
      message.error('Failed to update tier');
    }
    setActionLoading(false);
  };

  const handleAction = async () => {
    if (!reason && actionType === 'rejected') {
      message.warning('Please provide rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'products', selected.id), {
        status: actionType,
        rejectionReason: reason || '',
        approvedAt: actionType === 'approved' ? serverTimestamp() : null,
        approvedBy: actionType === 'approved' ? 'nksapcolouracademy@gmail.com' : '',
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'auditLogs'), {
        action: 'PRODUCT ' + actionType.toUpperCase(),
        details: 'Product: ' + selected.name + ' | Dealer: ' + selected.dealerName + (reason ? ' | Reason: ' + reason : ''),
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: selected.id, targetType: 'product',
        timestamp: serverTimestamp(),
      });
      setProducts(prev => prev.map(p =>
        p.id === selected.id ? { ...p, status: actionType, rejectionReason: reason } : p
      ));
      message.success('Product ' + actionType + ' — saved to Firebase');
      setActionModal(false); setViewModal(false); setReason('');
    } catch (error) {
      message.error('Action failed');
    }
    setActionLoading(false);
  };

  const columns = [
    {
      title: 'Product',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{r.name}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.dealerName} • {r.dealerCity}</div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (cat) => <span style={{ color: '#B0B8C1', fontSize: 12 }}>{cat}</span>,
    },
    {
      title: 'Price',
      render: (_, r) => (
        <div>
          <div style={{ color: '#C9A84C', fontWeight: 500 }}>Rs.{r.price}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.priceUnit}</div>
        </div>
      ),
    },
    {
      title: 'Tier',
      render: (_, r) => {
        const tier = TIER_CONFIG[r.assignedTier];
        const changed = r.assignedTier !== r.suggestedTier;
        if (!tier) return null;
        return (
          <div>
            <Tag style={{ background: tier.bg, color: tier.color, border: '0.5px solid ' + tier.color + '50', borderRadius: 20, fontSize: 11 }}>
              {tier.label}
            </Tag>
            {changed && <div style={{ fontSize: 10, color: '#FAAD14', marginTop: 2 }}>Changed from {TIER_CONFIG[r.suggestedTier]?.label}</div>}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
        return <Badge status={config.badge} text={<span style={{ color: config.color, fontSize: 12 }}>{config.label}</span>} />;
      },
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Tooltip title="View"><Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => { setSelected(r); setViewModal(true); }} /></Tooltip>
          <Tooltip title="Change Tier"><Button type="text" icon={<EditOutlined />} style={{ color: '#B0B8C1' }}
            onClick={() => { setSelected(r); setNewTier(r.assignedTier); setTierModal(true); }} /></Tooltip>
          {r.status === 'pending' && (
            <>
              <Tooltip title="Approve"><Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
                onClick={() => { setSelected(r); setActionType('approved'); setActionModal(true); }} /></Tooltip>
              <Tooltip title="Reject"><Button type="text" icon={<CloseCircleOutlined />} style={{ color: '#FF4D4F' }}
                onClick={() => { setSelected(r); setActionType('rejected'); setActionModal(true); }} /></Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Products', value: stats.total, color: '#C9A84C' },
          { title: 'Pending Review', value: stats.pending, color: '#FAAD14' },
          { title: 'Approved', value: stats.approved, color: '#52C41A' },
          { title: 'Rejected', value: stats.rejected, color: '#FF4D4F' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <Statistic title={<span style={{ color: '#7A8A96', fontSize: 12 }}>{stat.title}</span>}
                value={stat.value} valueStyle={{ color: stat.color, fontSize: 28, fontWeight: 600 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: '#7A8A96', fontSize: 13 }}>Filter:</span>
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 140 }}>
            <Option value="all">All Status</Option>
            <Option value="pending">Pending</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
          <Select value={filterCategory} onChange={setFilterCategory} style={{ width: 200 }}>
            <Option value="all">All Categories</Option>
            {categories.map(cat => <Option key={cat} value={cat}>{cat}</Option>)}
          </Select>
          <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }} onClick={loadProducts}>Refresh</Button>
          <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12 }}>
            {loading ? 'Loading...' : `${filtered.length} of ${products.length} products`}
          </span>
        </div>
      </Card>

      <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table dataSource={filtered} columns={columns} rowKey="id" pagination={{ pageSize: 10 }} />
        </Spin>
      </Card>

      {/* View Modal */}
      <Modal title={<span style={{ color: '#C9A84C' }}>Product Details - {selected?.name}</span>}
        open={viewModal} onCancel={() => setViewModal(false)} width={700}
        footer={
          selected?.status === 'pending' ? (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setViewModal(false)}>Close</Button>
              <Button danger icon={<CloseCircleOutlined />} onClick={() => { setActionType('rejected'); setActionModal(true); }}>Reject</Button>
              <Button icon={<CheckCircleOutlined />}
                style={{ background: 'linear-gradient(135deg, #E2C170, #9A7A2E)', border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                onClick={() => { setActionType('approved'); setActionModal(true); }}>Approve</Button>
            </div>
          ) : <Button onClick={() => setViewModal(false)}>Close</Button>
        }>
        {selected && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Product Name" span={2}>{selected.name}</Descriptions.Item>
            <Descriptions.Item label="Dealer">{selected.dealerName}</Descriptions.Item>
            <Descriptions.Item label="City">{selected.dealerCity}</Descriptions.Item>
            <Descriptions.Item label="Category" span={2}>{selected.category} — {selected.subCategory}</Descriptions.Item>
            <Descriptions.Item label="Brand">{selected.brand}</Descriptions.Item>
            <Descriptions.Item label="Price">Rs.{selected.price} {selected.priceUnit}</Descriptions.Item>
            <Descriptions.Item label="Stock">{selected.stock} units</Descriptions.Item>
            <Descriptions.Item label="Min Order">{selected.minOrderQty}</Descriptions.Item>
            <Descriptions.Item label="Suggested Tier">
              <Tag style={{ background: TIER_CONFIG[selected.suggestedTier]?.bg, color: TIER_CONFIG[selected.suggestedTier]?.color, border: 'none', borderRadius: 20 }}>
                {TIER_CONFIG[selected.suggestedTier]?.label}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Assigned Tier">
              <Tag style={{ background: TIER_CONFIG[selected.assignedTier]?.bg, color: TIER_CONFIG[selected.assignedTier]?.color, border: 'none', borderRadius: 20 }}>
                {TIER_CONFIG[selected.assignedTier]?.label}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>{selected.description}</Descriptions.Item>
            {selected.adminNotes && (
              <Descriptions.Item label="Admin Note" span={2}>
                <span style={{ color: '#FAAD14' }}>{selected.adminNotes}</span>
              </Descriptions.Item>
            )}
            {selected.rejectionReason && (
              <Descriptions.Item label="Rejection Reason" span={2}>
                <span style={{ color: '#FF4D4F' }}>{selected.rejectionReason}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Tier Modal */}
      <Modal title={<span style={{ color: '#C9A84C' }}>Change Tier - {selected?.name}</span>}
        open={tierModal} onCancel={() => { setTierModal(false); setNewTier(''); }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setTierModal(false); setNewTier(''); }}>Cancel</Button>
            <Button loading={actionLoading} onClick={handleTierChange}
              style={{ background: 'linear-gradient(135deg, #E2C170, #9A7A2E)', border: 'none', color: '#0D0D0D', fontWeight: 600 }}>
              Update Tier
            </Button>
          </div>
        }>
        <div style={{ marginBottom: 16, color: '#7A8A96', fontSize: 13 }}>
          Dealer suggested: <Tag style={{ background: TIER_CONFIG[selected?.suggestedTier]?.bg, color: TIER_CONFIG[selected?.suggestedTier]?.color, border: 'none', borderRadius: 20 }}>
            {TIER_CONFIG[selected?.suggestedTier]?.label}
          </Tag>
        </div>
        <Radio.Group value={newTier} onChange={e => setNewTier(e.target.value)} style={{ width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.entries(TIER_CONFIG).map(([key, val]) => (
              <Radio key={key} value={key}>
                <Tag style={{ background: val.bg, color: val.color, border: '0.5px solid ' + val.color + '50', borderRadius: 20, marginLeft: 8 }}>
                  {val.label}
                </Tag>
                {key === 'economy' && ' — Budget, local brands'}
                {key === 'premium' && ' — Trusted national brands'}
                {key === 'luxury' && ' — High-end, superior quality'}
                {key === 'super_luxury' && ' — Imported, exclusive'}
              </Radio>
            ))}
          </div>
        </Radio.Group>
      </Modal>

      {/* Action Modal */}
      <Modal
        title={<span style={{ color: actionType === 'approved' ? '#52C41A' : '#FF4D4F' }}>
          {actionType === 'approved' ? 'Approve' : 'Reject'} Product - {selected?.name}
        </span>}
        open={actionModal} onCancel={() => { setActionModal(false); setReason(''); }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setActionModal(false); setReason(''); }}>Cancel</Button>
            <Button loading={actionLoading} onClick={handleAction}
              style={{ background: actionType === 'approved' ? 'linear-gradient(135deg, #E2C170, #9A7A2E)' : '#FF4D4F', border: 'none', color: actionType === 'approved' ? '#0D0D0D' : '#fff', fontWeight: 600 }}>
              Confirm {actionType === 'approved' ? 'Approval' : 'Rejection'}
            </Button>
          </div>
        }>
        <div style={{ marginBottom: 16, color: '#B0B8C1' }}>
          {actionType === 'approved'
            ? 'Product will go live on HomeOwner app under ' + TIER_CONFIG[selected?.assignedTier]?.label + ' tier.'
            : 'Dealer will be notified to resubmit.'}
        </div>
        <TextArea rows={3} placeholder={actionType === 'approved' ? 'Optional notes...' : 'Required: Reason for rejection...'}
          value={reason} onChange={e => setReason(e.target.value)}
          style={{ background: '#222222', border: '0.5px solid rgba(201,168,76,0.3)', color: '#D4DCE3' }} />
      </Modal>
    </div>
  );
}